import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AttentionBoxWrapper,
  BottomWrapper,
  Content,
  InnerContainer,
  LoginContainer,
  LoginForm,
  Logo,
  LogoContainer,
  ResetPasswordText,
  Section,
} from "./styled";
import logo from "../../assets/img/logo-2.svg";
import Button from "../../library/button";
import { Title } from "../../library/title";
import { firebaseApp } from "../../firebase";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { LoginSchema, schema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Meta from "./meta";
import { FIELDS } from "./fields";
import { useAuth } from "../../AuthContext";
import { useEffect, useState } from "react";
import { fetchUserDetailsAPI } from "../../auth";
import {
  LOGIN_ERROR_CONTENT_MAP,
  LoginErrorContent,
  mapToLoginErrorCode,
} from "./errors";
import AttentionBox from "../../library/attention-box";

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, clearError, logout } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm<LoginSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const auth = getAuth(firebaseApp);
  const [loginError, setLoginError] = useState<LoginErrorContent | null>(null);

  useEffect(() => {
    const from = location.state?.from?.pathname || "/dashboard/transcripts";
    if (currentUser) {
      navigate(from, { replace: true });
    }
  }, [currentUser, navigate, location.state]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  useEffect(() => {
    const errorCodeFromState = location.state?.errorCode;

    if (errorCodeFromState) {
      const errorContent = LOGIN_ERROR_CONTENT_MAP.get(errorCodeFromState);
      if (errorContent) {
        setLoginError(errorContent);
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const performLogin = async (data: LoginSchema) => {
    clearError();

    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      await fetchUserDetailsAPI();
    } catch (err: any) {
      await logout();

      const errorCode = mapToLoginErrorCode(err?.code);

      if (errorCode === "invalid_credentials") {
        setError("email", { message: " " });
        setError("password", { message: "Feil e-post eller passord" });
      } else {
        const errorContent = LOGIN_ERROR_CONTENT_MAP.get(errorCode);
        setLoginError(errorContent || null);
      }

      setValue("password", "");
    }
  };

  const renderError = () => {
    if (!loginError) return null;
    return (
      <AttentionBoxWrapper>
        <AttentionBox
          icon="error"
          type="fail"
          title={loginError.title}
          text={loginError.text}
        />
      </AttentionBoxWrapper>
    );
  };

  return (
    <>
      <Meta />
      <Section>
        <InnerContainer>
          <Content>
            <LogoContainer>
              <Link to="/">
                <Logo src={logo} />
              </Link>
            </LogoContainer>
            {renderError()}
            <LoginContainer>
              <Title>{t("login.title")}</Title>
              <LoginForm onSubmit={handleSubmit(performLogin)}>
                {FIELDS.map((field) => field.renderField(register, errors))}
                <Button
                  className="login-button"
                  type="submit"
                  variant="regular"
                  color="blue"
                  disabled={isSubmitting}
                  ariaLabel={t("login.form.login-button.ariaLabel") as string}
                >
                  {isSubmitting
                    ? "Logger inn.."
                    : t("login.form.login-button.title")}
                </Button>
                <BottomWrapper>
                  <Link
                    aria-label={
                      t("login.form.register-link.ariaLabel") as string
                    }
                    to="/register"
                  >
                    <Button type="button" variant="regular" color="grey">
                      {t("login.form.register-button.title")}
                    </Button>
                  </Link>
                  <ResetPasswordText>
                    {t("login.form.forgot-password.text")}{" "}
                    <Link
                      aria-label={
                        t("login.form.forgot-password.link.ariaLabel") as string
                      }
                      to="/forgot-password"
                    >
                      {t("login.form.forgot-password.link.title")}
                    </Link>
                    .
                  </ResetPasswordText>
                </BottomWrapper>
              </LoginForm>
            </LoginContainer>
          </Content>
        </InnerContainer>
      </Section>
    </>
  );
};

export default Login;
